import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import { ClientClient, EnvironmentClient } from '../../api/api';
import useApiClient from '../../common/hooks/useApiClient';
import { Permissions } from '../../common/Permissions';
import { useCurrentUserPermissions } from '../CurrentUserPermissionsContext';

export type ArchitectDeploymentAction = 'deployComponent' | 'deployClient' | 'removeClient' | 'rotateClientSecret';
const actions: Record<ArchitectDeploymentAction, string> = {
	deployComponent: 'Deploy component version', deployClient: 'Deploy client', removeClient: 'Remove client', rotateClientSecret: 'Rotate client secret',
};
type Plan = { action: ArchitectDeploymentAction; environmentId: string; environmentName: string; targetId: string; targetName: string; version?: string };

/** Approval executes the frozen request through existing authorized APIs, never through a model boolean. */
export default function ArchitectDeploymentDialog({ onClose }: { onClose: () => void }) {
	const environments = useApiClient(EnvironmentClient);
	const clients = useApiClient(ClientClient);
	const { hasPermission } = useCurrentUserPermissions();
	const allowed = hasPermission(Permissions.EnvironmentRead) && hasPermission(Permissions.EnvironmentDeploy);
	const [action, setAction] = useState<ArchitectDeploymentAction>('deployComponent');
	const [environmentId, setEnvironmentId] = useState('');
	const [targetId, setTargetId] = useState('');
	const [version, setVersion] = useState('');
	const [plan, setPlan] = useState<Plan>();
	const [pending, setPending] = useState(false);
	const inFlight = useRef(false);
	const [result, setResult] = useState<string>();
	const [error, setError] = useState<string>();
	const options = useQuery({ queryKey: ['architect-deployment-options', action], enabled: allowed, gcTime: 0, retry: false,
		queryFn: async () => {
			// Explicit paging: don't silently limit the review picker to the first page.
			const envs = [] as { id: string; name: string }[];
			const targets = [] as { id: string; name: string }[];
			for (let pageIndex = 0; pageIndex < 20; pageIndex++) {
				const page = await environments.search({ paging: { pageIndex, pageSize: 100, sortAscending: true } });
				envs.push(...page.items);
				if (!page.hasNextPage) break;
				if (pageIndex === 19) throw new Error('Too many environments for this picker. Use the Environments view.');
			}
			for (let pageIndex = 0; pageIndex < 20; pageIndex++) {
				const page = action === 'deployComponent'
					? await environments.searchComponent({ paging: { pageIndex, pageSize: 100, sortAscending: true } })
					: await clients.search({ paging: { pageIndex, pageSize: 100, sortAscending: true } });
				targets.push(...page.items);
				if (!page.hasNextPage) break;
				if (pageIndex === 19) throw new Error('Too many targets for this picker. Use the Environments view.');
			}
			return { envs, targets };
		},
	});
	const review = () => {
		const environment = options.data?.envs.find((item) => item.id === environmentId);
		const target = options.data?.targets.find((item) => item.id === targetId);
		if (!allowed || !environment || !target || (action === 'deployComponent' && (!version.trim() || version.trim().toLowerCase() === 'latest'))) return;
		setPlan({ action, environmentId, environmentName: environment.name, targetId, targetName: target.name, version: action === 'deployComponent' ? version.trim() : undefined });
	};
	const approve = async () => {
		if (!allowed || !plan || inFlight.current || result || error) return;
		inFlight.current = true;
		setPending(true);
		try {
			const response = plan.action === 'deployComponent'
				? await environments.addEnvironmentComponent(plan.environmentId, { componentId: plan.targetId, containerImageVersion: plan.version! })
				: plan.action === 'deployClient'
					? await environments.addEnvironmentClient(plan.environmentId, { clientId: plan.targetId })
					: plan.action === 'removeClient'
						? await environments.removeEnvironmentClient(plan.environmentId, plan.targetId)
						: await environments.rotateEnvironmentClientSecret(plan.environmentId, plan.targetId);
			setResult(response.releasePlanId);
		} catch {
			setError('The request failed or its outcome is uncertain. Check release plans before submitting another request; it may already have been accepted.');
		} finally { setPending(false); }
	};
	return <Dialog open onClose={pending ? undefined : onClose} fullWidth maxWidth="sm">
		<DialogTitle>Review Architect deployment</DialogTitle>
		<DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
			<Alert severity="warning">This explicit approval affects only the action below. Design/Implement chat mode never grants deployment approval.</Alert>
			{!allowed && <Alert severity="error">Environment Read and Deploy permissions are required.</Alert>}
			{(options.isError || error) && <Alert severity="error">{error ?? 'Could not load authorized deployment targets.'}</Alert>}
			{!plan ? <>
				<TextField select label="Deployment action" value={action} onChange={(event) => { setAction(event.target.value as ArchitectDeploymentAction); setTargetId(''); }}>
					{Object.entries(actions).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
				</TextField>
				<TextField select label="Environment" value={environmentId} onChange={(event) => setEnvironmentId(event.target.value)} disabled={!allowed || options.isLoading}>
					{options.data?.envs.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
				</TextField>
				<TextField select label="Deployment target" value={targetId} onChange={(event) => setTargetId(event.target.value)} disabled={!allowed || options.isLoading}>
					{options.data?.targets.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
				</TextField>
				{action === 'deployComponent' && <TextField label="Exact image version" value={version} onChange={(event) => setVersion(event.target.value)} helperText="Use the exact version verified by the Architect; do not use latest." />}
			</> : <>
				<Typography>{actions[plan.action]}</Typography>
				<Typography>Environment: {plan.environmentName} ({plan.environmentId})</Typography>
				<Typography>Target: {plan.targetName} ({plan.targetId})</Typography>
				{plan.version && <Typography>Version: {plan.version}</Typography>}
			</>}
			{result && <Alert severity="success">Release plan created: {result}. This is not confirmation that deployment completed. <Button component={Link} to="/environments" onClick={onClose}>View release plans</Button></Alert>}
		</Stack></DialogContent>
		<DialogActions><Button onClick={onClose} disabled={pending}>Close</Button>
			{plan && !result && !error && <Button disabled={pending} onClick={() => setPlan(undefined)}>Edit request</Button>}
			{!plan ? <Button onClick={review} disabled={!allowed || !targetId || !environmentId || options.isError || options.isFetching || (action === 'deployComponent' && (!version.trim() || version.trim().toLowerCase() === 'latest'))}>Review exact action</Button>
				: <Button color="warning" variant="contained" onClick={() => void approve()} disabled={!allowed || pending || !!result || !!error}>Approve and create release plan</Button>}
		</DialogActions>
	</Dialog>;
}
